/**
 * Document Processing Limits and Constraints
 * Prevents abuse and ensures performance when handling large documents
 */

export const DOCUMENT_LIMITS = {
  // Hard file size limits (before conversion)
  MAX_FILE_SIZE: 10 * 1024 * 1024,        // 10MB max upload
  WARN_FILE_SIZE: 5 * 1024 * 1024,        // 5MB warning threshold
  
  // Markdown content limits (after conversion)
  MAX_MARKDOWN_SIZE: 2 * 1024 * 1024,     // 2MB markdown max
  MAX_TOKENS: 500000,                      // ~500k tokens max
  
  // Display limits
  DIRECT_DISPLAY_TOKENS: 2000,            // ~8KB - show full content
  CHUNKED_DISPLAY_TOKENS: 20000,          // ~80KB - show with sections
  MAX_CHUNK_SIZE: 1000,                   // ~4KB per chunk
  
  // Processing limits
  CONVERSION_TIMEOUT: 30000,              // 30 seconds max conversion
  MAX_SECTIONS: 100,                      // Max 100 sections in TOC
  
  // Preview limits
  SECTION_PREVIEW_LENGTH: 200,            // Characters in section preview
  INITIAL_DISPLAY_SECTIONS: 10,          // Number of sections to show initially
};

/**
 * Processing strategy based on token count
 */
export type ProcessingStrategy = 
  | 'FULL_DISPLAY'        // Show everything (< 2k tokens)
  | 'CHUNKED'             // Split into sections (2k-20k tokens)
  | 'COLLAPSED_SECTIONS'  // Show TOC + previews (20k-100k tokens)
  | 'SUMMARY_ONLY';       // Show summary, load on demand (> 100k tokens)

/**
 * Determine processing strategy based on token count
 */
export function getProcessingStrategy(tokens: number): ProcessingStrategy {
  if (tokens < DOCUMENT_LIMITS.DIRECT_DISPLAY_TOKENS) {
    return 'FULL_DISPLAY';
  } else if (tokens < DOCUMENT_LIMITS.CHUNKED_DISPLAY_TOKENS) {
    return 'CHUNKED';
  } else if (tokens < 100000) {
    return 'COLLAPSED_SECTIONS';
  } else {
    return 'SUMMARY_ONLY';
  }
}

/**
 * Estimate token count from text
 * Rough approximation: 1 token ≈ 4 characters
 */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Estimate reading time in minutes
 * Average reading speed: 200 words per minute
 */
export function estimateReadingTime(text: string): number {
  const words = text.split(/\s+/).length;
  return Math.ceil(words / 200);
}
